import type { Config } from "wagmi"
import { formatUnits } from "viem"

import type { IEventBus } from "@/domains/events"
import type { OrchestratedQueryStore } from "@/domains/library"
import type { IOrchestratedQuerySender } from "@/domains/library/orchestrated-query-interfaces"
import type { LockOnChainTransport } from "@/domains/lock/lock-on-chain-transport"
import type { ITokenLookup } from "@/domains/tokens/types"
import { LcAddress } from "@/domains/core"
import { OrchestratedQuery } from "@/domains/library"

import type { VenammStore } from "./venamm-store"
import { VenammSupplyUpdatedEvent } from "./events"

export class VenammOrchestrator {
  private loadAvailableTokenSupplyQuery: OrchestratedQuery<bigint> | undefined

  constructor(
    private readonly venammStore: VenammStore,
    private readonly lockOnChainTransport: LockOnChainTransport,
    private readonly tokenLookup: ITokenLookup,
    private readonly orchestratedQueryStore: OrchestratedQueryStore,
    private readonly eventBus: IEventBus,
  ) {}

  initialize(loadAvailableTokenSupplyQuery: OrchestratedQuery<bigint>): void {
    this.loadAvailableTokenSupplyQuery = loadAvailableTokenSupplyQuery
  }

  async onLoadAvailableTokenSupplyTrigger(_query: IOrchestratedQuerySender<bigint>): Promise<bigint> {
    const result = await this.lockOnChainTransport.getVenammAvailableTokenSupply()
    if (result === null) {
      return 0n
    }
    return result
  }

  onLoadAvailableTokenSupplyQuerySuccess(_sender: IOrchestratedQuerySender<bigint>, data: bigint): void {
    try {
      // Get governance token decimals for value calculation
      const governanceTokenAddress = this.lockOnChainTransport["addresses"].governanceToken
      const governanceToken = this.tokenLookup.find(new LcAddress(governanceTokenAddress))
      const decimals = governanceToken?.decimals ?? 18

      const value = Number(formatUnits(data, decimals))

      this.venammStore.updateAvailableTokenSupply(data, value)

      // Emit event
      this.eventBus.publish(new VenammSupplyUpdatedEvent(data, value))
    } catch (error) {
      console.error("Error processing Venamm available token supply:", error)
    }
  }

  onLoadAvailableTokenSupplyQueryFailed(_sender: IOrchestratedQuerySender<bigint>, error: Error): void {
    console.error("Failed to load Venamm available token supply:", error)
  }
}
